// frontend/src/components/tax/TaxEvidenceList.tsx
//
// TAX-PARSE-2 — list of TaxEvidence items with an "Add to MIR" action on
// each. Row treatment mirrors FindingsTable.tsx's ScoreBadge styling.

import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/ui/empty-state'
import { formatApiError } from '@/utils/httpErrors'
import { taxEvidenceToMir, type TaxEvidenceItem } from '@/api/tax'

function fmtFieldName(key: string): string {
  return key
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ')
}

function fmtUsd(n: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(n)
}

function ConfidenceBadge({ confidence }: { confidence: number }) {
  const pct = Math.round(confidence * 100)
  const styles =
    confidence >= 0.85
      ? 'bg-green-50 text-green-800 border-green-200'
      : confidence >= 0.6
        ? 'bg-amber-50 text-amber-800 border-amber-200'
        : 'bg-red-50 text-red-800 border-red-200'
  return (
    <span className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-semibold tracking-wide ${styles}`}>
      {pct}%
    </span>
  )
}

interface EvidenceRowProps {
  item: TaxEvidenceItem
  dealId: string
  onAdded: (item: TaxEvidenceItem, findingNumber: number) => void
}

function EvidenceRow({ item, dealId, onAdded }: EvidenceRowProps) {
  const [submitting, setSubmitting] = useState(false)
  const [added, setAdded] = useState<number | null>(null)

  const handleAddToMir = async () => {
    setSubmitting(true)
    try {
      const result = await taxEvidenceToMir(item.id, dealId)
      setAdded(result.number)
      onAdded(item, result.number)
      toast.success(`Added to MIR — Finding #${result.number} (${result.score})`)
    } catch (err) {
      toast.error(formatApiError(err))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-white shadow-sm p-4 flex flex-wrap items-start gap-3">
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium text-gray-900">{fmtFieldName(item.field_name)}</span>
          {item.flagged && (
            <span className="rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-red-700 border border-red-200">
              Flagged
            </span>
          )}
          <ConfidenceBadge confidence={item.confidence} />
        </div>
        <p className="mt-0.5 text-sm text-gray-500">
          {fmtUsd(item.value)} · {item.form_type.toUpperCase()}
          {item.tax_year ? ` · FY${item.tax_year}` : ''}
        </p>
        {item.notes && (
          <p className="mt-1 text-xs text-muted-foreground italic">{item.notes}</p>
        )}
      </div>
      <div className="shrink-0">
        {added != null ? (
          <span className="text-xs font-medium text-green-700">✓ Finding #{added}</span>
        ) : (
          <Button variant="outline" size="sm" onClick={handleAddToMir} disabled={submitting}>
            {submitting ? 'Adding…' : 'Add to MIR'}
          </Button>
        )}
      </div>
    </div>
  )
}

interface TaxEvidenceListProps {
  dealId: string
  evidence: TaxEvidenceItem[]
  onAdded?: (item: TaxEvidenceItem, findingNumber: number) => void
}

export function TaxEvidenceList({ dealId, evidence, onAdded }: TaxEvidenceListProps) {
  if (evidence.length === 0) {
    return (
      <EmptyState
        title="No tax evidence yet"
        description="Upload and extract a tax document to generate evidence items here."
      />
    )
  }

  return (
    <div className="space-y-3">
      {evidence.map((item) => (
        <EvidenceRow
          key={item.id}
          item={item}
          dealId={dealId}
          onAdded={(it, num) => onAdded?.(it, num)}
        />
      ))}
    </div>
  )
}

export default TaxEvidenceList
